import type { PopoverOrigin } from '@mui/material';
import { Divider, MenuItem, Paper, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import { INTEGRATION_PROVIDERS, SYNC_STATUSES, type PlanBillingData, type SyncStatus } from '@weft/types';
import { isNotEmptyString,
  isNotNullOrUndefined,
  validateNotNullOrUndefined } from '@weft/utils';
import { VySwitch } from 'apps/app/src/components/new-design/inputs/VySwitch';
import { useContractPage } from 'apps/app/src/hooks/requests/pages/use-contract-page';
import { prefetchContractProductsOverviews } from 'apps/app/src/hooks/requests/use-contract-products-overviews';
import { useConnectedIntegrations } from 'apps/app/src/hooks/requests/use-integrations';
import { prefetchNetSuiteUsers } from 'apps/app/src/hooks/requests/use-net-suite';
import { useSoftUpdatePlan } from 'apps/app/src/hooks/requests/use-update-soft-plan';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useMenu } from '../../../../hooks/use-menu';
import { DeleteContractMenuItem } from './contract-page-header-menu-components/DeleteContractMenuItem';
import { MenuItemContractRenewal } from './contract-page-header-menu-components/MenuItemContractRenewal';
import { MenuItemNetSuiteExportContract } from './contract-page-header-menu-components/MenuItemNetSuiteExportContract';
import { MenuItemReviseContract } from './contract-page-header-menu-components/MenuItemReviseContract';
import { MenuItemTerminateContract } from './contract-page-header-menu-components/MenuItemTerminateContract';
import { MenuItemUpdateContract } from './contract-page-header-menu-components/MenuItemUpdateContract';
import { DEFAULT_MENU_ANCHOR_ORIGIN, DEFAULT_MENU_TRANSFORM_ORIGIN } from './contract-page-header-menu-consts/contract-page-default-menu';
import { UpdateContractStatusMenuItem } from './UpdateContractStatusMenuItem';

type ContractPageHeaderMenuProps = {
  children?: React.ReactElement;
  isActive: boolean;
  isTerminated: boolean;
  contractId: string;
  planId: string;
  planName: string;
  billingData: PlanBillingData;
  customerName: string;
  contractStartDate: Date;
  syncStatus: SyncStatus;
  contractEndDate?: Date;
  contractName: string;
  menuAnchorOrigin?: PopoverOrigin;
  menuTransformOrigin?: PopoverOrigin;
};

export const ContractPageHeaderMenu: React.FC<ContractPageHeaderMenuProps> = ({
  children,
  isActive,
  contractId,
  planId,
  planName,
  billingData,
  customerName,
  syncStatus,
  contractStartDate,
  contractEndDate,
  isTerminated,
  contractName,
  menuAnchorOrigin,
  menuTransformOrigin,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  
  const queryClient = useQueryClient();
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const { data: connectedIntegrations } = useConnectedIntegrations({
    suspense: true,
  });
  validateNotNullOrUndefined(connectedIntegrations, 'connectedIntegrations');
  const isConnectedToNetSuite = connectedIntegrations.includes(INTEGRATION_PROVIDERS.netSuite);
  const updatePlanMutation = useSoftUpdatePlan(planId);

  const invalidateRelatedQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        return ['contract', contractId, planId, 'contracts-overviews'].some(
          (key) => query.queryKey.includes(key),
        );
      },
    });
  };
          
  const handleToggle = async () => {
    await runMutation(
      updatePlanMutation,
      {
        name: planName,
        billingData: {
          ...billingData,
          autoRenewal: !billingData.autoRenewal,
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to update plan',
      }, {
        onSuccess: invalidateRelatedQueries,
      });
  };
  
  const { data: contractPageResult } = useContractPage(contractId, { enabled: shouldPrefetch });  

  const erpId = contractPageResult?.contractPageData.contract.customer.erpMetadata?.erpId;  
  const isSync = syncStatus == SYNC_STATUSES.Synced;
  
  useQuery({
    queryKey: [contractId, erpId],
    queryFn: async () => {
      await prefetchContractProductsOverviews(contractId, queryClient);
  
      if (isNotEmptyString(erpId)) {
        await prefetchNetSuiteUsers(erpId, queryClient);
        setShouldPrefetch(false);
      }
    },
    enabled: shouldPrefetch, 
  });

  const handleMouseOver = () => {
    if (isConnectedToNetSuite) {
      setShouldPrefetch(true);
    }
  };

  return (
    <>
      {isNotNullOrUndefined(children) &&
        React.cloneElement(children, {
          onClick: handleMenuOpen,
          onMouseOver:handleMouseOver,
        })}

      <Menu
        anchorOrigin={menuAnchorOrigin ?? DEFAULT_MENU_ANCHOR_ORIGIN}
        transformOrigin={menuTransformOrigin ?? DEFAULT_MENU_TRANSFORM_ORIGIN}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        MenuListProps={{
          className: 'py-0',
        }}
        slotProps={{
          paper: {
            className: 'max-w-[250px] w-full shadow-xl',
          },
        }}
      >
        <Paper elevation={8}>
          {isTerminated && contractEndDate && (
            <MenuItemContractRenewal
              contractId={contractId}
              contractEndDate={contractEndDate}
            />
          )}

          {!isTerminated && (
            <div>
              <MenuItem
                className="flex items-center justify-between pt-2"
                disabled={updatePlanMutation.isLoading}
              >
                <Typography>Auto renew</Typography>

                <VySwitch
                  checked={billingData.autoRenewal}
                  onClick={handleToggle}
                />
              </MenuItem>

              <Divider className="mt-0" />
            </div>
          )}

          <UpdateContractStatusMenuItem
            disabled={isTerminated}
            contractId={contractId}
            isActive={isActive}
            onClick={handleMenuClose}
          />

          <MenuItemUpdateContract
            contractId={contractId}
            contractStartDate={contractStartDate}
            billingInterval={billingData.billingInterval}
            disabled={isTerminated}
          />

          <MenuItemReviseContract
            contractId={contractId}
            disabled={isTerminated}
          />

          <MenuItemTerminateContract
            contractId={contractId}
            customerName={customerName}
            isActive={isActive}
          />

          <Divider />
          <MenuItemNetSuiteExportContract
            planId={planId}
            contractId={contractId}
            disabled={!isConnectedToNetSuite}
            isSynced={isSync}
          />
          <Divider />

          <DeleteContractMenuItem
            contractId={contractId}
            contractName={contractName}
          />
        </Paper>
      </Menu>
    </>
  );
};
